import React, { useEffect, useState } from "react";
import { Landmark, Users, AlertTriangle, TrendingUp, Send, Megaphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#2D5A27", "#E05E36", "#DDA77B", "#8B5E34", "#6b7280", "#0e7490"];
const STATUS_LABELS = { submitted: "Submitted", acknowledged: "Ack.", in_progress: "In progress", resolved: "Resolved", rejected: "Rejected" };

export default function GovAdmin() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [city, setCity] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [broadcast, setBroadcast] = useState({ title: "", body: "", kind: "emergency", city: "" });
  const [error, setError] = useState(null);

  useSeo({
    title: "Officials Dashboard · find2near",
    description: "City admin analytics — complaint queues, ward performance and emergency broadcasts.",
    canonical: "/gov/admin",
  });

  const refresh = () => {
    setBusy(true); setError(null);
    api.get("/gov/analytics", { params: { days, city: city || undefined } })
       .then((r) => setData(r.data))
       .catch((e) => setError(e.response?.data?.detail || "Access denied"))
       .finally(() => setBusy(false));
  };

  useEffect(() => { if (user) refresh(); }, [user, days, city]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendBroadcast = async () => {
    if (!broadcast.title.trim() || !broadcast.body.trim()) { toast.error("Title + body required"); return; }
    try {
      const r = await api.post("/gov/broadcast", broadcast);
      toast.success(`Broadcast fanned out to ${r.data.fanout_users} users`);
      setBroadcast({ title: "", body: "", kind: "emergency", city: "" });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-6">
        <Landmark className="mx-auto text-[#2D5A27]" size={44} />
        <h2 className="heading text-2xl font-extrabold mt-4">Officials only</h2>
        <p className="text-stone-500 mt-2 text-sm">Sign in with an official account to view the analytics dashboard.</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-6">
        <AlertTriangle className="mx-auto text-red-500" size={44} />
        <h2 className="heading text-2xl font-extrabold mt-4">{error}</h2>
        <p className="text-stone-500 mt-2 text-sm">Ask a super-admin to add you to <code>db.gov_officials</code>.</p>
      </div>
    );
  }
  if (!data) {
    return <div className="p-12 text-center text-stone-500"><Loader2 className="mx-auto animate-spin" /> Loading analytics…</div>;
  }

  const g = data.grievances;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="gov-admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Landmark size={12} /> Officials Dashboard</div>
          <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">City ops — <span className="text-[#2D5A27]">at a glance.</span></h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Filter by city" className="px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="admin-city" />
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="px-3 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="admin-days">
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
        </div>
      </div>

      {/* Top counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total complaints" value={g.total} icon={<TrendingUp className="text-emerald-600" size={16} />} testid="kpi-total" />
        <Kpi label="Open" value={g.open} icon={<AlertTriangle className="text-amber-500" size={16} />} testid="kpi-open" />
        <Kpi label="Resolved" value={g.resolved} icon={<Users className="text-sky-600" size={16} />} testid="kpi-resolved" />
        <Kpi label="Resolved %" value={`${g.resolved_pct}%`} icon={<TrendingUp className="text-fuchsia-600" size={16} />} testid="kpi-pct" />
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-3xl bg-white border border-stone-200 p-5">
          <h3 className="heading text-base font-black text-stone-900 mb-3">Daily inflow</h3>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={g.daily_inflow} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <XAxis dataKey="day" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#2D5A27" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-stone-200 p-5">
          <h3 className="heading text-base font-black text-stone-900 mb-3">By status</h3>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={g.by_status} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label={(p) => STATUS_LABELS[p.status] || p.status}>
                  {g.by_status.map((entry, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Ward attention list */}
      <div className="rounded-3xl bg-white border border-stone-200 p-5" data-testid="admin-wards">
        <h3 className="heading text-base font-black text-stone-900 mb-3">Ward attention</h3>
        {g.ward_attention.length === 0 ? (
          <div className="text-sm text-stone-400 py-6 text-center">No ward-tagged complaints in this range.</div>
        ) : (
          <ul className="space-y-2">
            {g.ward_attention.map((w) => (
              <li key={w.ward} className="grid grid-cols-[100px_1fr_100px_60px] items-center gap-3 text-sm" data-testid={`ward-${w.ward}`}>
                <span className="font-black text-stone-800 truncate">{w.ward}</span>
                <div className="h-3 rounded-full bg-stone-100 overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${Math.round((w.resolved / (w.total || 1)) * 100)}%` }} />
                </div>
                <span className="text-xs text-stone-500 text-right">{w.resolved}/{w.total} resolved</span>
                <span className="text-xs font-black text-amber-700 text-right">{w.open} open</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Broadcast */}
      <div className="rounded-3xl bg-gradient-to-br from-[#E05E36] to-red-700 text-white p-5 md:p-6" data-testid="admin-broadcast">
        <div className="flex items-center gap-2 mb-3">
          <Megaphone size={20} />
          <h3 className="heading text-xl font-black">Emergency broadcast</h3>
        </div>
        <p className="text-white/70 text-sm mb-3">Post a public notice AND push it to every citizen's in-app notifications inbox.</p>
        <div className="grid gap-2 md:grid-cols-2">
          <input value={broadcast.title} onChange={(e) => setBroadcast({ ...broadcast, title: e.target.value })} placeholder="Title" className="px-4 py-2 rounded-full text-stone-900 text-sm outline-none" data-testid="broadcast-title" />
          <select value={broadcast.kind} onChange={(e) => setBroadcast({ ...broadcast, kind: e.target.value })} className="px-4 py-2 rounded-full text-stone-900 text-sm outline-none" data-testid="broadcast-kind">
            {["emergency", "utility", "health", "policy", "announcement"].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <input value={broadcast.city} onChange={(e) => setBroadcast({ ...broadcast, city: e.target.value })} placeholder="City (blank = all cities)" className="mt-2 w-full px-4 py-2 rounded-full text-stone-900 text-sm outline-none" data-testid="broadcast-city" />
        <textarea value={broadcast.body} onChange={(e) => setBroadcast({ ...broadcast, body: e.target.value })} placeholder="Message body" rows={3} className="mt-2 w-full px-4 py-2 rounded-2xl text-stone-900 text-sm outline-none" data-testid="broadcast-body" />
        <button onClick={sendBroadcast} className="mt-3 bg-white text-[#E05E36] font-black px-5 py-2.5 rounded-full inline-flex items-center gap-2 hover:scale-105 transition-transform" data-testid="broadcast-send">
          <Send size={14} /> Broadcast now
        </button>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-stone-200 px-4 py-3" data-testid={testid}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-black text-stone-500">
        <span>{label}</span>{icon}
      </div>
      <div className="heading text-2xl font-black text-stone-900 tabular-nums mt-1">{value}</div>
    </div>
  );
}
